<template>
  <div class="content">
    <div class="container-fluid">
       <div class="row">
          <div class="col-md-12">
             <div class="card">
                <div class="card-header d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                   <h4 class="card-title m-0 mb-3 mb-md-0">Sales Dashboard</h4>
                   <button @click="scrollToPurchaseQueue" class="btn purchase-queue-btn shadow-sm px-4 justify-content-center header-action-btn" title="Go to Purchase Queue" style="border-radius: 50px; font-weight: 600; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px; height: 38px; display: flex; align-items: center;">
                      <i class="fas fa-list-alt mr-2"></i> Purchase Queue
                   </button>
                </div>
                <div class="card-body">
                   <div class="d-flex align-items-start flex-wrap justify-content-between">
                      <div class="d-flex align-items-center flex-wrap">
                        <div class="control-group mr-3 mb-2">
                            <label class="control-label text-muted small text-uppercase font-weight-bold mr-2 mb-0">Range</label>
                            <div class="select-wrapper">
                                <select v-model="selectedDateRange" @change="onDateRangeChange" class="custom-select-modern">
                                    <option value="today">Today</option>
                                    <option value="yesterday">Yesterday</option>
                                    <option value="last7days">Last 7 Days</option>
                                    <option value="last30days">Last 30 Days</option>
                                    <option value="thisMonth">This Month</option>
                                    <option value="lastMonth">Last Month</option>
                                    <option value="yearToDate">Year to Date</option>
                                    <option value="custom">Custom Range</option>
                                </select>
                                <i class="fas fa-chevron-down select-icon"></i>
                            </div>
                        </div>
                        
                        <div v-if="selectedDateRange === 'custom'" class="date-inputs-group mr-3 mb-2 row no-gutters align-items-center bg-light rounded px-2 py-1">
                            <input type="date" v-model="customStartDate" @change="processData" class="form-control-plaintext border-0 bg-transparent py-0 px-2" style="width: 140px; font-size: 15px;">
                            <span class="text-muted small mx-1 font-weight-bold">to</span>
                            <input type="date" v-model="customEndDate" @change="processData" class="form-control-plaintext border-0 bg-transparent py-0 px-2" style="width: 140px; font-size: 15px;">
                        </div>
                      </div>

                      <div class="d-flex align-items-center flex-wrap justify-content-end">
                              <div class="control-group mr-3 mb-2">
                                <label class="control-label text-muted small text-uppercase font-weight-bold mr-2 mb-0">Refresh</label>
                                <div class="select-wrapper">
                                    <select v-model="refreshInterval" @change="handleRefreshChange" class="custom-select-modern">
                                        <option v-for="opt in refreshOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                                    </select>
                                    <i class="fas fa-chevron-down select-icon"></i>
                                </div>
                              </div>

                              <button @click="fetchSalesData" class="btn btn-primary btn-round btn-icon shadow-sm mb-2" :disabled="isLoading" style="width: 42px; height: 42px; border-radius: 50%; padding: 0; display: flex; align-items: center; justify-content: center;">
                                  <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
                              </button>
                      </div>
                   </div>
                </div>
             </div>
          </div>
       </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center" @click="showMetrics = !showMetrics" style="cursor: pointer; flex-grow: 1;">
                     <h4 class="card-title m-0">Performance Overview</h4>
                </div>
                <div class="d-flex align-items-center">
                    <button class="btn btn-link p-0 mr-3 text-muted" @click.stop="showFinancials = !showFinancials" title="Toggle Financials" style="font-size: 1.2rem;">
                        <i class="fas" :class="showFinancials ? 'fa-eye' : 'fa-eye-slash'"></i>
                    </button>
                    <i class="fas" :class="showMetrics ? 'fa-chevron-up' : 'fa-chevron-down'" @click="showMetrics = !showMetrics" style="cursor: pointer"></i>
                </div>
            </div>
            <div class="card-body" v-if="showMetrics">

    <!-- Sales Summary and Market Share -->
    <div class="row">
      <div class="col-xl-4 col-sm-6">
          <div class="card card-stats h-100">
              <div class="card-body py-2 px-3">
                  <div class="row">
                      <div class="col-4">
                          <div class="icon-big text-center icon-warning">
                              <i class="fas fa-dollar-sign text-success"></i>
                          </div>
                      </div>
                      <div class="col-8">
                          <div class="numbers">
                              <p class="card-category">{{ dateRangeLabel }} Sales</p>
                              <h4 class="card-title text-nowrap" v-if="showFinancials">${{ summary.sales.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</h4>
                              <h4 class="card-title text-nowrap text-muted" v-else>###</h4>
                              <p class="text-muted mb-0" style="font-size: 0.9rem;">
                                  ROI: <strong>
                                    <template v-if="showFinancials">
                                      {{ (summary.sales > 0 ? ((summary.profit / summary.sales) * 100).toFixed(1) : '0.0') }}%
                                    </template>
                                    <template v-else>
                                      ###
                                    </template>
                                  </strong>
                              </p>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      </div>

      <div class="col-xl-4 col-sm-6">
          <div class="card card-stats h-100">
              <div class="card-body py-2 px-3">
                  <div class="row">
                      <div class="col-4">
                          <div class="icon-big text-center icon-warning">
                              <i class="fas fa-chart-line text-success"></i>
                          </div>
                      </div>
                      <div class="col-8">
                          <div class="numbers">
                              <p class="card-category">{{ dateRangeLabel }} Estimated Profit</p>
                              <template v-if="showFinancials">
                                <h4 class="card-title text-nowrap mb-1">${{ summary.profit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</h4>
                                <p class="text-muted mb-0" style="font-size: 0.9rem;">
                                    Avg: <strong>${{ (summary.tickets > 0 ? (summary.profit / summary.tickets) : 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</strong> / ticket
                                </p>
                              </template>
                              <h4 class="card-title text-nowrap mb-1 text-muted" v-else>###</h4>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      </div>

      <div class="col-xl-4 col-sm-6">
          <div class="card card-stats h-100">
              <div class="card-body py-2 px-3">
                  <div class="row">
                      <div class="col-4">
                          <div class="icon-big text-center icon-warning">
                              <i class="fas fa-ticket-alt text-primary"></i>
                          </div>
                      </div>
                      <div class="col-8">
                          <div class="numbers">
                              <p class="card-category">Tickets Sold</p>
                              <h4 class="card-title text-nowrap">{{ summary.tickets.toLocaleString() }}</h4>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      </div>
    </div>

    <!-- Donut Charts Row -->
    <div class="row mt-4">
        <div class="col-md-4">
            <div class="card">
                <div class="card-header">
                    <h4 class="card-title">Exchange Totals</h4>
                </div>
                <div class="card-body">
                     <donut-chart v-if="exchangeDonutData" :chart-data="exchangeDonutData" :options="donutOptions" :height="180"></donut-chart>
                     <div v-else class="text-center p-4">No data available</div>
                </div>
            </div>
        </div>
        <div class="col-md-4">
            <div class="card">
                <div class="card-header">
                    <h4 class="card-title">Primary Totals</h4>
                </div>
                <div class="card-body">
                     <donut-chart v-if="primaryDonutData" :chart-data="primaryDonutData" :options="donutOptions" :height="180"></donut-chart>
                     <div v-else class="text-center p-4">No data available</div>
                </div>
            </div>
        </div>
        <div class="col-md-4">
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                     <h4 class="card-title">{{ volumeChartsMode === 'weekly' ? 'Weekly Volume' : 'Hourly Volume' }}</h4>
                     <div class="btn-group">
                         <button class="btn btn-sm btn-xs" :class="volumeChartsMode === 'weekly' ? 'btn-primary' : 'btn-secondary'" @click.prevent="volumeChartsMode = 'weekly'" title="Weekly">W</button>
                         <button class="btn btn-sm btn-xs" :class="volumeChartsMode === 'hourly' ? 'btn-primary' : 'btn-secondary'" @click.prevent="volumeChartsMode = 'hourly'" title="Hourly">H</button>
                     </div>
                </div>
                <div class="card-body">
                     <template v-if="volumeChartsMode === 'weekly'">
                        <profit-bar-chart v-if="weeklyVolumeChartData" :chart-data="weeklyVolumeChartData" :options="smallBarChartOptions" :height="180"></profit-bar-chart>
                        <div v-else class="text-center p-4">No data available</div>
                     </template>
                     <template v-else>
                        <profit-bar-chart v-if="hourlyVolumeChartData" :chart-data="hourlyVolumeChartData" :options="stackedBarChartOptions" :height="180"></profit-bar-chart>
                        <div v-else class="text-center p-4">No data available</div>
                     </template>
                </div>
            </div>
        </div>
    </div>
            </div>
        </div>
      </div>
    </div>

    <!-- Sales Transactions Table -->
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header d-flex flex-column flex-md-row justify-content-between align-items-md-center">
             <div class="mb-3 mb-md-0 w-100 text-left">
                <h4 class="card-title">Sales Transactions</h4>
                <p class="card-category">Sales & Fulfillment Status</p>
             </div>
             <div class="d-flex flex-column flex-md-row align-items-stretch align-items-md-center w-100 w-md-auto">
                <div class="position-relative mr-0 mr-md-3 mb-2 mb-md-0 flex-grow-1" style="min-width: 140px; max-width: 180px;">
                  <button class="btn btn-white border d-flex align-items-center justify-content-between shadow-sm w-100 status-filter-btn" type="button" @click="showStatusFilter = !showStatusFilter">
                        <div class="d-flex align-items-center text-truncate">
                            <i class="fas fa-filter text-muted mr-2"></i>
                            <span class="text-dark font-weight-bold">{{ statusFilterLabel }}</span>
                        </div>
                        <i class="fas fa-chevron-down small text-muted ml-2"></i>
                    </button>
                    
                    <!-- Backdrop -->
                    <div v-if="showStatusFilter" @click="showStatusFilter = false" style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: 1040; cursor: default;"></div>
                    
                    <!-- Menu -->
                    <div v-if="showStatusFilter" class="bg-white border-0 rounded-lg shadow-lg p-2 status-filter-dropdown" style="position: absolute; top: 110%; left: 0; z-index: 1050; max-width: 200px; width: 100%; min-width: 140px; max-height: 400px; overflow-y: auto;">
                        <h6 class="dropdown-header text-muted text-uppercase font-weight-bold px-3 py-2 small">Filter Status</h6>
                        <div v-for="opt in statusOptions" :key="opt.value" 
                             class="d-flex align-items-center px-3 py-3 rounded status-option mb-1" 
                             :class="salesTxStatusFilter.includes(opt.value) ? 'bg-light text-primary font-weight-bold' : 'text-dark'"
                             style="cursor: pointer; transition: all 0.2s;" 
                             @click.prevent="toggleStatusFilter(opt.value)">
                            
                            <i class="fas fa-lg mr-3" :class="salesTxStatusFilter.includes(opt.value) ? 'fa-check-square text-primary' : 'fa-square text-secondary opacity-5'"></i>
                            
                            <span style="font-size: 15px; user-select: none;">
                                {{ opt.text }}
                            </span>
                        </div>
                        
                        <div class="border-top pt-2 mt-2 px-2">
                             <button class="btn btn-sm btn-block btn-info text-white font-weight-bold py-2" @click="showStatusFilter = false">
                                 Done
                             </button>
                        </div>
                    </div>
                </div>

                <div class="flex-grow-1" style="min-width: 200px;">
                    <input type="text" class="form-control shadow-sm border-0 w-100" placeholder="Search ref, name, venue..." v-model="salesTxSearchQuery" style="height: 45px; border-radius: 8px; background-color: #f8f9fa;">
                </div>

                <div v-if="salesTxSearchQuery || (salesTxStatusFilter.length > 0 && !salesTxStatusFilter.includes('all'))" class="ml-2">
                    <button class="btn btn-outline-danger btn-icon btn-round shadow-sm" @click="resetFilters" title="Clear Filters" style="width: 45px; height: 45px;">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
             </div>
          </div>
          <div class="card-body bg-light pt-3 pb-3">
             <!-- Loading State -->
             <div v-if="salesTransactionsLoading && salesTransactions.length === 0" class="text-center py-5">
                 <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                 </div>
                 <div class="mt-2 text-muted">Loading transactions...</div>
             </div>

             <!-- Transactions List -->
             <div v-else class="transaction-list">
                <div v-for="(tx, index) in paginatedSalesTx" 
                     :key="tx.sync_order_id ? `${tx.sync_order_id}_${index}` : index"
                     class="card border-0 mb-3 shadow-sm transaction-card"
                     @click="showDetails(tx)">
                     
                    <div class="card-body p-3">
                        <div class="row align-items-center">
                            
                            <!-- Left: Status & Event -->
                            <div class="col-lg-4 col-md-5 col-12 mb-2 mb-md-0">
                                <div class="d-flex align-items-center mb-1">
                                    <span class="badge mr-3 px-2 py-1" 
                                          :class="getBadgeClass(resolveStatus(tx))" style="font-size: 75%;">
                                        <i class="fas mr-1" :class="getBadgeIcon(resolveStatus(tx))"></i>
                                        {{ resolveStatus(tx) }}
                                    </span>
                                    <span class="badge badge-soft-secondary text-muted border" style="font-size: 75%;">
                                        {{ tx.customerDisplayName }}
                                    </span>
                                </div>
                                <h6 class="font-weight-bold text-dark mb-1 text-truncate" style="font-size: 1.05rem; max-width: 100%;">
                                    {{ tx.eventName }}
                                </h6>
                                <div class="text-muted small" style="font-size: 0.9rem;">
                                    <i class="far fa-calendar-alt mr-1 text-primary-soft"></i> {{ formatDate(tx.eventDate) }}
                                    <span class="mx-2 text-light-gray">|</span>
                                    <i class="fas fa-map-marker-alt mr-1 text-primary-soft"></i> {{ tx.venue }}
                                </div>
                                <div class="text-muted small mt-1" v-if="tx.external_invoice_ref">
                                    <span class="text-xs text-uppercase font-weight-bold opacity-7" style="font-size: 0.75rem;">Ref:</span>
                                    <span class="d-inline-flex align-items-center">
                                      <span class="text-monospace" style="font-size: 0.85rem;">{{ tx.external_invoice_ref }}</span>
                                      <button class="btn btn-light border shadow-sm d-inline-flex align-items-center justify-content-center ml-2" 
                                              type="button" 
                                              @click.stop="copyToClipboard(tx.external_invoice_ref)" 
                                              title="Copy Ref" 
                                              style="width: 22px; height: 22px; border-radius: 6px; padding: 0;">
                                        <i class="far fa-copy text-muted" style="font-size: 11px;"></i>
                                      </button>
                                    </span>
                                </div>
                                <div class="text-danger small mt-1" v-if="resolveStatus(tx) === 'ATTENTION' && tx.pw_parking_pass_url" style="font-size: 0.85rem; word-break: break-all;">
                                    <i class="fas fa-exclamation-circle mr-1"></i> {{ tx.pw_parking_pass_url }}
                                </div>
                            </div>

                            <!-- Middle: Ticket Info -->
                            <div class="col-lg-3 col-md-3 col-6 border-left-lg pl-lg-4">
                                <div class="row no-gutters">
                                    <div class="col-auto mr-3">
                                        <div class="text-center bg-soft-primary rounded p-2" style="min-width: 60px;">
                                            <div class="h4 font-weight-bold text-primary mb-0" style="line-height: 1;">{{ tx.quantity }}</div>
                                            <div class="very-small text-uppercase font-weight-bold text-primary opacity-7" style="font-size: 0.75rem;">Qty</div>
                                        </div>
                                    </div>
                                    <div class="col">
                                        <div class="font-weight-bold mb-0 text-dark" style="font-size: 1rem;">{{ tx.section || 'GA' }}</div>
                                        <div class="small text-muted mb-1" style="font-size: 0.85rem;">Section</div>
                                        <div class="badge badge-light border text-muted font-weight-normal text-truncate" style="max-width: 100%; font-size: 0.85rem;">
                                           <i class="fas fa-parking mr-1 text-muted"></i> {{ tx.primary_name || tx.primary_location_name }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Right: Financials -->
                            <div class="col-lg-3 col-md-2 col-6 text-right">
                                <div class="text-muted small text-uppercase" style="font-size: 0.75rem;">List Price</div>
                                <div class="h5 font-weight-bold text-dark mb-1">{{ formatCurrency(tx.listPrice) }}</div>
                               <!-- If profit exists -->
                                <div v-if="tx.profit !== undefined">
                                     <div class="text-muted very-small text-uppercase" style="font-size: 0.75rem;">Profit</div>
                                     <template v-if="showFinancials">
                                         <div class="font-weight-bold mb-0" 
                                              :class="Number(tx.profit) >= 0 ? 'text-success' : 'text-danger'" style="font-size: 1rem;">
                                             {{ formatCurrency(tx.profit) }}
                                         </div>
                                     </template>
                                     <div v-else class="font-weight-bold mb-0 text-muted" style="font-size: 1rem;">####</div>
                                </div>
                            </div>

                            <!-- Actions -->
                            <div class="col-lg-2 col-md-2 col-12 d-flex flex-row flex-md-column justify-content-between align-items-end mt-3 mt-md-0 border-left-lg pl-lg-3">
                              <div class="text-right mb-auto">
                                <div class="text-muted font-weight-bold" style="line-height: 1.2;">{{ formatDateShort(tx.invoiceDate) }}</div>
                                <div class="small text-muted opacity-7" style="font-size: 0.85rem;">{{ formatTime(tx.invoiceDate) }}</div>
                              </div>
                                
                                <div class="d-flex align-items-center ml-auto ml-md-0 mt-2">
                                    <a v-if="tx.pw_parking_pass_url && String(tx.pw_parking_pass_url).startsWith('http')" 
                                       :href="tx.pw_parking_pass_url" 
                                       target="_blank" 
                                       @click.stop
                                       class="btn btn-info btn-icon btn-round shadow-sm mr-2" 
                                       title="Get Pass" style="width: 40px; height: 40px;">
                                        <i class="fas fa-ticket-alt"></i>
                                    </a>
                                    
                                    <button class="btn btn-light btn-icon btn-round border shadow-sm" title="More Details" style="width: 40px; height: 40px;">
                                        <i class="fas fa-ellipsis-h text-muted"></i>
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
                
                <div v-if="filteredSalesTransactions.length === 0" class="text-center py-5 text-muted">
                    <i class="far fa-folder-open fa-3x mb-3 opacity-50"></i>
                    <p>No transactions found matching your criteria.</p>
                </div>
             </div>
             
             <!-- Pagination (Modern) -->
             <div v-if="salesTransactions.length > 0" class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 pt-3">
                <div class="d-flex align-items-center mb-3 mb-md-0">
                    <span class="text-muted small mr-2">Show:</span>
                    <select v-model="salesTxPerPage" @change="salesTxCurrentPage = 1" class="custom-select custom-select-sm shadow-none border-0 bg-white mr-3" style="width: auto; font-weight: bold; color: #4e73df;">
                        <option v-for="size in salesTxPerPageOptions" :key="size" :value="size">{{ size }}</option>
                    </select>
                    <span class="text-muted small">
                      {{ filteredSalesTransactions.length === 0 ? 0 : salesTxStartIndex + 1 }} - {{ salesTxEndIndex }} of {{ filteredSalesTransactions.length }}
                    </span>
                </div>
                <div>
                  <button 
                    @click="salesTxPreviousPage" 
                    :disabled="salesTxCurrentPage === 1"
                    class="btn btn-white border shadow-sm btn-sm mr-2 rounded-pill px-3"
                  >
                    <i class="fas fa-chevron-left mr-1"></i> Prev
                  </button>
                  <span class="mx-2 small font-weight-bold text-muted">Page {{ salesTxCurrentPage }} / {{ salesTxTotalPages }}</span>
                  <button 
                    @click="salesTxNextPage" 
                    :disabled="salesTxCurrentPage === salesTxTotalPages"
                    class="btn btn-white border shadow-sm btn-sm ml-2 rounded-pill px-3"
                  >
                    Next <i class="fas fa-chevron-right ml-1"></i>
                  </button>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Purchase Queue Section -->
    <div class="row" id="purchase-queue-section">
      <div class="col-12">
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center" @click="showPurchaseQueue = !showPurchaseQueue" style="cursor: pointer">
                <h4 class="card-title m-0">Purchase Queue</h4>
                <i class="fas" :class="showPurchaseQueue ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>
            <div class="card-body" v-if="showPurchaseQueue">
                 <purchase-queue class="p-0"></purchase-queue>
            </div>
        </div>
      </div>
    </div>

    <!-- Transaction Details Modal -->
    <b-modal id="transaction-details-modal" hide-header hide-footer size="xl" centered body-class="p-0">
        <div v-if="selectedTransaction">
            <!-- Header -->
            <div class="position-relative bg-white p-4 pb-0 d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="text-uppercase text-muted font-weight-bold mb-2 small">Transaction Details</h6>
                  <h3 class="font-weight-bold mb-2 pr-5">{{ selectedTransaction.eventName }}</h3>
                  <div class="h5 text-muted font-weight-normal">
                      <i class="fas fa-map-marker-alt mr-2 text-primary"></i>{{ selectedTransaction.venue }}
                      <span class="mx-2 text-light">|</span>
                      <i class="far fa-calendar-alt mr-2 text-primary"></i>{{ formatDate(selectedTransaction.eventDate) }}
                  </div>
                  <div v-if="selectedTransaction.section" class="h5 text-muted font-weight-normal mt-1">
                      <i class="fas fa-chair mr-2 text-primary"></i>Section: {{ selectedTransaction.section }}
                  </div>
                </div>
                
                <div v-if="!isEditingTransaction">
                   <button class="btn btn-outline-danger shadow-sm mr-2" @click="confirmDeleteTransaction">
                      <i class="fas fa-trash-alt mr-2"></i> Delete
                   </button>
                   <button class="btn btn-outline-primary shadow-sm" @click="startEditingTransaction">
                      <i class="fas fa-edit mr-2"></i> Edit Record
                   </button>
                </div>
            </div>

            <!-- View Mode -->
            <div class="p-4" v-if="!isEditingTransaction">
                <!-- Status Badges -->
                <div class="row mb-4">
                    <div class="col-sm-6 mb-3 mb-sm-0">
                        <div class="card shadow-sm border-0 h-100 mb-0" :class="`bg-soft-${getStatusVariant(resolveStatus(selectedTransaction))}`">
                            <div class="card-body p-3 d-flex align-items-center">
                                <div class="icon-circle mr-3" :class="`text-${getStatusVariant(resolveStatus(selectedTransaction))} bg-white`">
                                    <i class="fas" :class="getBadgeIcon(resolveStatus(selectedTransaction))"></i>
                                </div>
                                <div>
                                    <div class="small text-uppercase font-weight-bold opacity-7" :class="`text-${getStatusVariant(resolveStatus(selectedTransaction))}`">Status</div>
                                    <div class="h6 font-weight-bold mb-0 text-dark">
                                        {{ resolveStatus(selectedTransaction) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-sm-6">
                        <div class="card shadow-sm border-0 h-100 mb-0 bg-soft-info">
                            <div class="card-body p-3 d-flex align-items-center">
                                <div class="icon-circle mr-3 text-info bg-white">
                                    <i class="fas fa-ticket-alt"></i>
                                </div>
                                <div>
                                    <div class="small text-uppercase font-weight-bold text-info opacity-7">Exchange</div>
                                    <div class="h6 font-weight-bold mb-0 text-dark">
                                        {{ selectedTransaction.customerDisplayName }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <!-- Main Info Column -->
                    <div class="col-lg-8">
                        <!-- Financial Cards -->
                        <div class="row mb-4">
                            <div class="col-sm-6 mb-3 mb-sm-0">
                                <div class="p-4 bg-light rounded h-100 shadow-sm" style="border-left: 5px solid #28a745;">
                                    <div class="text-muted small text-uppercase font-weight-bold mb-1">Total Profit</div>
                                    <div class="h2 font-weight-bold mb-0" 
                                         :class="Number(selectedTransaction.profit) >= 0 ? 'text-success' : 'text-danger'">
                                        ${{ Number(selectedTransaction.profit).toFixed(2) }}
                                    </div>
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <div class="p-4 bg-light rounded h-100 shadow-sm" style="border-left: 5px solid #17a2b8;">
                                    <div class="text-muted small text-uppercase font-weight-bold mb-1">List Price</div>
                                    <div class="h2 font-weight-bold mb-0 text-dark">
                                        ${{ Number(selectedTransaction.listPrice).toFixed(2) }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Details Grid -->
                        <h6 class="text-muted text-uppercase mb-3 small font-weight-bold border-bottom pb-2">Order Information</h6>
                        <div class="row">
                            <div class="col-sm-6 mb-3">
                                <div class="text-muted small text-uppercase font-weight-bold">Quantity</div>
                                <div class="h5 font-weight-normal mb-0">{{ selectedTransaction.quantity }} Tickets</div>
                            </div>
                            <div class="col-sm-6 mb-3">
                                <div class="text-muted small text-uppercase font-weight-bold">Primary Source</div>
                                <div class="h5 font-weight-normal mb-0">{{ selectedTransaction.primary_name || selectedTransaction.primary_location_name }}</div>
                            </div>
                            <div class="col-sm-12 mb-3">
                                <div class="text-muted small text-uppercase font-weight-bold">Invoice Reference</div>
                                <div class="d-inline-flex align-items-center">
                                  <div class="font-weight-bold text-monospace bg-light p-2 rounded d-inline-block mr-2">{{ selectedTransaction.external_invoice_ref }}</div>
                                  <button class="btn btn-light border shadow-sm d-inline-flex align-items-center justify-content-center" 
                                          type="button" 
                                          @click.stop="copyToClipboard(selectedTransaction.external_invoice_ref)" 
                                          title="Copy Ref" 
                                          style="width: 28px; height: 28px; border-radius: 6px; padding: 0;">
                                    <i class="far fa-copy text-muted" style="font-size: 13px;"></i>
                                  </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Actions Column -->
                    <div class="col-lg-4">
                        <h6 class="text-muted text-uppercase mb-3 small font-weight-bold border-bottom pb-2">Actions</h6>
                        <div class="d-flex flex-column">
                            <a v-if="selectedTransaction.pw_parking_pass_url && String(selectedTransaction.pw_parking_pass_url).startsWith('http')" 
                               :href="selectedTransaction.pw_parking_pass_url" 
                               target="_blank" 
                               class="btn btn-primary btn-lg btn-block text-left mb-3 shadow-sm d-flex justify-content-between align-items-center p-3">
                                <span><i class="fas fa-ticket-alt mr-3"></i>View Pass</span>
                                <i class="fas fa-chevron-right opacity-50"></i>
                            </a>
                            
                            <a v-if="selectedTransaction.primary_location_url" 
                               :href="selectedTransaction.primary_location_url" 
                               target="_blank" 
                               class="btn btn-info btn-lg btn-block text-left shadow-sm d-flex justify-content-between align-items-center p-3 text-white">
                                <span><i class="fas fa-map-marked-alt mr-3"></i>{{ selectedTransaction.section || 'Location Info' }}</span>
                                <i class="fas fa-external-link-alt opacity-50"></i>
                            </a>
                            
                            <div v-if="(!selectedTransaction.pw_parking_pass_url || !String(selectedTransaction.pw_parking_pass_url).startsWith('http')) && !selectedTransaction.primary_location_url" class="alert alert-light text-center text-muted">
                                No external links available
                            </div>
                        </div>

                        <div class="mt-4 pt-3 border-top">
                            <details>
                                <summary class="text-muted small cursor-pointer" style="cursor: pointer;">Raw API Data</summary>
                                <div class="bg-light p-2 rounded mt-2">
                                    <pre class="small mb-0 text-muted" style="max-height: 150px; overflow: auto; font-size: 10px;">{{ JSON.stringify(selectedTransaction, null, 2) }}</pre>
                                </div>
                            </details>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Edit Mode -->
            <div class="p-4" v-else>
               <div class="alert alert-info border-0 shadow-sm">
                  <i class="fas fa-info-circle mr-2"></i> Update transaction details below.
               </div>
               
               <div class="form-group mb-4">
                  <label class="font-weight-bold text-muted small text-uppercase">Status</label>
                  <select v-model="editTransactionForm.sync_order_status" class="form-control custom-select shadow-sm" style="height: 45px;">
                      <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value === 'all' ? '' : opt.value" :disabled="opt.value === 'all'">
                          {{ opt.text }}
                      </option>
                  </select>
               </div>
               
               <div class="form-group mb-4">
                  <label class="font-weight-bold text-muted small text-uppercase">Parking Pass URL</label>
                  <div class="position-relative">
                      <input type="text" v-model="editTransactionForm.pw_parking_pass_url" class="form-control shadow-sm" placeholder="https://..." style="height: 45px; padding-right: 50px;">
                      <button class="btn btn-light border shadow-sm position-absolute d-flex align-items-center justify-content-center" 
                              type="button" 
                              @click="copyToClipboard(editTransactionForm.pw_parking_pass_url)" 
                              title="Copy URL" 
                              style="right: 8px; top: 50%; transform: translateY(-50%); width: 32px; height: 32px; border-radius: 6px; z-index: 5; padding: 0;">
                          <i class="far fa-copy text-muted" style="font-size: 14px;"></i>
                      </button>
                  </div>
                  <small class="form-text text-muted">The direct link to the parking pass or ticket.</small>
               </div>
               
               <div class="form-group mb-4">
                  <label class="font-weight-bold text-muted small text-uppercase">Parking Pass ID / External ID</label>
                  <input type="text" v-model="editTransactionForm.pw_parking_pass_id" class="form-control shadow-sm" placeholder="Order ID or Pass ID" style="height: 45px;">
               </div>
            </div>
            
            <div class="bg-light p-3 d-flex justify-content-end align-items-center border-top">
                <template v-if="!isEditingTransaction">
                  <button class="btn btn-link text-muted mr-3" @click="$bvModal.hide('transaction-details-modal')">Close</button>
                  <button class="btn btn-secondary px-4 shadow-sm" @click="$bvModal.hide('transaction-details-modal')">Done</button>
                </template>
                <template v-else>
                  <button class="btn btn-link text-danger mr-3" @click="cancelEditingTransaction" :disabled="isSavingTransaction">Cancel</button>
                  <button class="btn btn-success px-4 shadow-sm d-flex align-items-center" @click="saveTransaction" :disabled="isSavingTransaction">
                      <i v-if="isSavingTransaction" class="fas fa-spinner fa-spin mr-2"></i>
                      {{ isSavingTransaction ? 'Saving...' : 'Save Changes' }}
                  </button>
                </template>
            </div>
        </div>
    </b-modal>

    <!-- Trend Chart -->
    <div class="row">
      <div class="col-md-12">
         <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <div>
                   <h4 class="card-title">Est. Profit Trend</h4>
                   <p class="card-category">Daily Profit & Sales</p>
                </div>
                <div>
                   <select v-model="chartDateRange" @change="processChartDataOnly" class="form-control" style="width: auto; height: 35px; padding: 5px 10px;">
                        <option value="last7days">Last 7 Days</option>
                        <option value="last30days">Last 30 Days</option>
                        <option value="last3Months">Last 3 Months</option>
                        <option value="last6Months">Last 6 Months</option>
                        <option value="thisYear">This Year</option>
                   </select>
                </div>
            </div>
            <div class="card-body">
                <profit-bar-chart v-if="trendChartData" :chart-data="trendChartData" :options="chartOptions" :height="300"></profit-bar-chart>
                <div v-else class="text-center p-4">No chart data available</div>
            </div>
         </div>
      </div>
    </div>


    </div> 

    <!-- Back to Top Button -->
    <transition name="fade">
      <button v-show="showBackToTop" @click="scrollToTop" class="btn btn-primary btn-icon back-to-top shadow-lg" title="Back to Top">
        <i class="fas fa-arrow-up"></i>
      </button>
    </transition>
  </div> 
</template>

<script>
import ProfitBarChart from './ProfitBarChart.vue';
import DonutChart from './DonutChart.vue';
import PurchaseQueue from './PurchaseQueue.vue';

export default {
  name: 'SalesDashboard',
  components: {
    ProfitBarChart,
    DonutChart,
    PurchaseQueue
  },
  data() {
    return {
      salesData: [],
      filteredSales: [],
      isLoading: false,
      showMetrics: true,
      showPurchaseQueue: true,
      showBackToTop: false,
      errorMessage: '',
      summary: {
        sales: 0,
        profit: 0,
        tickets: 0
      },
      chartData: null,
      trendChartData: null,
      weeklyVolumeChartData: null,
      hourlyVolumeChartData: null,
      volumeChartsMode: 'weekly',
      exchangeDonutData: null,
      primaryDonutData: null,
      donutOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: 'right',
            labels: {
                boxWidth: 12
            }
        },
        tooltips: {
            callbacks: {
                label: function(tooltipItem, data) {
                    const dataset = data.datasets[tooltipItem.datasetIndex];
                    const total = dataset.data.reduce((acc, curr) => acc + curr, 0);
                    const currentValue = dataset.data[tooltipItem.index];
                    const percentage = total > 0 ? ((currentValue / total) * 100).toFixed(1) + '%' : '0.0%';

                    const label = data.labels[tooltipItem.index] || '';
                    // Remove currency formatting as this is volume data
                    return label + ': ' + currentValue.toLocaleString() + ' (' + percentage + ')';
                }
            }
        }
      },
      // Minimal options for the small bar chart
      smallBarChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
            xAxes: [{
                display: true, // Show basic labels
                gridLines: { display: false },
                ticks: { fontSize: 9 }
            }],
            yAxes: [{
                display: true, // Show y axis for volume
                ticks: {
                  beginAtZero: true,
                  precision: 0,
                  callback: function(value) {
                    return value;
                  }
                }
            }]
        },
        tooltips: {
            callbacks: {
                label: function(tooltipItem) {
                  return tooltipItem.yLabel;
                }
            }
        }
      },
      stackedBarChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false }, 
        scales: {
            xAxes: [{
                stacked: true,
                display: true,
                gridLines: { display: false },
                ticks: { fontSize: 9 }
            }],
            yAxes: [{
                stacked: true,
                display: true,
                ticks: {
                  beginAtZero: true,
                  precision: 0,
                  callback: function(value) {
                    return value;
                  }
                }
            }]
        },
        tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
                label: function(tooltipItem, data) {
                  const dataset = data.datasets[tooltipItem.datasetIndex];
                  const val = dataset.data[tooltipItem.index];
                  if (!val) return null;
                  return dataset.label + ': ' + val;
                }
            }
        }
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    callback: function(value) {
                        return '$' + value;
                    }
                }
            }]
        },
        tooltips: {
            callbacks: {
                label: function(tooltipItem) {
                    return '$' + tooltipItem.yLabel.toFixed(2);
                }
            }
        }
      },
      selectedDateRange: 'today',
      chartDateRange: 'last3Months', // Separate filter for chart
      customStartDate: '',
      
      salesTransactions: [],
      salesTransactionsLoading: false,
      salesTxSearchQuery: '',
      salesTxStatusFilter: ['all'],
      showStatusFilter: false,
      statusOptions: [
        { value: 'all', text: 'All Statuses' },
        { value: 'DELIVERED', text: 'Delivered' },
        { value: 'CONFIRMED', text: 'Confirmed' },
        { value: 'PENDING', text: 'Pending' },
        { value: 'NOT DELIVERED', text: 'Not Delivered' },
        { value: 'ATTENTION', text: 'Attention' }
      ],
      selectedTransaction: null,
      
      // Sales Transactions Pagination
      salesTxCurrentPage: 1,
      salesTxPerPage: 25,
      salesTxPerPageOptions: [10, 25, 50, 100],

      // Auto-refresh settings
      refreshInterval: 60000, // Default 1 minute
      refreshOptions: [
        { value: 0, text: 'Off' },
        { value: 10000, text: '10 seconds' },
        { value: 30000, text: '30 seconds' },
        { value: 60000, text: '1 minute' },
        { value: 300000, text: '5 minutes' },
        { value: 600000, text: '10 minutes' }
      ],
      timer: null,
      customEndDate: '',
      showFinancials: true,
      
      // Editing
      isEditingTransaction: false,
      isSavingTransaction: false,
      editTransactionForm: {
        pw_parking_pass_url: '',
        sync_order_status: '',
        pw_parking_pass_id: ''
      }
    };
  },
  computed: {
    filteredSalesTransactions() {
      let result = this.salesTransactions;

      // 0. Filter by Status (Client-Side)
      if (this.salesTxStatusFilter && this.salesTxStatusFilter.length > 0 && !this.salesTxStatusFilter.includes('all')) {
          result = result.filter(tx => {
              const status = this.resolveStatus(tx);
              return this.salesTxStatusFilter.includes(status);
          });
      }

      // 1. Filter by Date Range (Client-Side strict ET filtering)
      const { startDate, endDate } = this.getDateRange();
      if (startDate && endDate) {
         result = result.filter(tx => {
             const dStr = tx.invoiceDate || tx.created_at; 
             if (!dStr) return false;
             
             // Convert record timestamp to ET YYYY-MM-DD
             // The API returns "Fake UTC" (wall-clock ET time ending in Z). 
             // We trust the date string's YYYY-MM-DD part exactly as is.
             try {
                const etDateStr = dStr.substring(0, 10); 
                return etDateStr >= startDate && etDateStr <= endDate;
             } catch(e) { return false; }
         });
      }

      // 2. Filter by Search Query
      if (this.salesTxSearchQuery) {
        const query = this.salesTxSearchQuery.toLowerCase();
        result = result.filter(tx => {
            return (
            (tx.external_invoice_ref && String(tx.external_invoice_ref).toLowerCase().includes(query)) ||
            (tx.primary_name && String(tx.primary_name).toLowerCase().includes(query)) ||
            (tx.customerDisplayName && String(tx.customerDisplayName).toLowerCase().includes(query)) ||
            (tx.primary_location_name && String(tx.primary_location_name).toLowerCase().includes(query)) ||
            (tx.venue && String(tx.venue).toLowerCase().includes(query)) ||
            (tx.eventName && String(tx.eventName).toLowerCase().includes(query)) ||
            (tx.section && String(tx.section).toLowerCase().includes(query))
            );
        });
      }
      return result;
    },

    // Sales Transactions Pagination Computed
    salesTxTotalPages() {
      return Math.ceil(this.filteredSalesTransactions.length / this.salesTxPerPage) || 1;
    },
    salesTxStartIndex() {
      return (this.salesTxCurrentPage - 1) * this.salesTxPerPage;
    },
    salesTxEndIndex() {
      return Math.min(this.salesTxStartIndex + this.salesTxPerPage, this.filteredSalesTransactions.length);
    },
    paginatedSalesTx() {
      return this.filteredSalesTransactions.slice(this.salesTxStartIndex, this.salesTxEndIndex);
    },

    dateRangeLabel() {
      const labels = {
        today: "Today's",
        yesterday: "Yesterday's",
        last7days: "Last 7 Days",
        last30days: "Last 30 Days",
        thisMonth: "This Month's",
        lastMonth: "Last Month's",
        yearToDate: "Year to Date",
        custom: "Custom Range"
      };
      return labels[this.selectedDateRange] || "Sales";
    },
    statusFilterLabel() {
        if (this.salesTxStatusFilter.includes('all') || this.salesTxStatusFilter.length === 0) {
            return 'All Statuses';
        }
        if (this.salesTxStatusFilter.length === 1) {
            const opt = this.statusOptions.find(o => o.value === this.salesTxStatusFilter[0]);
            return opt ? opt.text : this.salesTxStatusFilter[0];
        }
        return `${this.salesTxStatusFilter.length} Selected`;
    }
  },
  watch: {
    salesTxSearchQuery() {
      this.salesTxCurrentPage = 1;
    },
    salesTxStatusFilter() {
      this.salesTxCurrentPage = 1;
    }
  },
  methods: {
    startTimer() {
      this.stopTimer();
      if (this.refreshInterval > 0) {
        this.timer = setInterval(this.fetchSalesData, this.refreshInterval);
      }
    },
    
    toggleStatusFilter(val) {
        if (val === 'all') {
            this.salesTxStatusFilter = ['all'];
        } else {
            // Remove 'all' if present
            let newFilters = this.salesTxStatusFilter.filter(f => f !== 'all');
            
            if (newFilters.includes(val)) {
                newFilters = newFilters.filter(f => f !== val);
            } else {
                newFilters.push(val);
            }
            
            // If empty, revert to all
            if (newFilters.length === 0) {
                newFilters = ['all'];
            }
            this.salesTxStatusFilter = newFilters;
        }
    },
    
    resetFilters() {
        this.salesTxStatusFilter = ['all'];
        this.salesTxSearchQuery = '';
    },

    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    scrollToPurchaseQueue() {
      this.showPurchaseQueue = true;
      this.$nextTick(() => {
        const el = document.getElementById('purchase-queue-section');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },

    handleRefreshChange() {
      this.startTimer();
    },

    showDetails(tx) {
        this.selectedTransaction = tx;
        this.isEditingTransaction = false;
        this.$bvModal.show('transaction-details-modal');
    },

    startEditingTransaction() {
        this.editTransactionForm = {
            pw_parking_pass_url: this.selectedTransaction.pw_parking_pass_url || '',
            sync_order_status: this.resolveStatus(this.selectedTransaction),
            pw_parking_pass_id: this.selectedTransaction.pw_parking_pass_id || ''
        };
        this.isEditingTransaction = true;
    },

    cancelEditingTransaction() {
        this.isEditingTransaction = false;
        this.editTransactionForm = {
            pw_parking_pass_url: '',
            sync_order_status: '',
            pw_parking_pass_id: ''
        };
    },

    async saveTransaction() {
        if (!this.selectedTransaction) return;
        
        // Try to find the valid ID. Use ticketId (if exists) or sync_order_id as fallback
        const id = this.selectedTransaction.ticketId || this.selectedTransaction.sync_order_id;
        
        if (!id) {
            this.makeToast('Cannot identify transaction ID for update.', { type: 'error' });
            return;
        }

        this.isSavingTransaction = true;
        
        try {
            // Note: Assuming endpoint is on the configured API host
            // If running locally, you might need to adjust this URL or setup proxy
            const url = `https://api.spotjockeys.com/api/sales/${id}`;
            
            const response = await this.$http.put(url, this.editTransactionForm);
            
            // Update the local model
            const updatedData = response.data || this.editTransactionForm;
            Object.assign(this.selectedTransaction, updatedData);
            
            this.makeToast('Transaction updated successfully', { type: 'success' });
            
            this.isEditingTransaction = false;
        } catch (error) {
            console.error('Update failed:', error);
            this.makeToast('Failed to update transaction', { type: 'error' });
        } finally {
            this.isSavingTransaction = false;
        }
    },

    async confirmDeleteTransaction() {
        if (!this.selectedTransaction) return;
        
        const confirmResult = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this transaction? This action cannot be undone.', {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes, Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        });

        if (confirmResult) {
            this.deleteTransaction();
        }
    },

    async deleteTransaction() {
        if (!this.selectedTransaction) return;
        
        const id = this.selectedTransaction.ticketId || this.selectedTransaction.sync_order_id;
        
        if (!id) {
            this.makeToast('Cannot identify transaction ID for deletion.', { type: 'error' });
            return;
        }

        try {
            const url = `https://api.spotjockeys.com/api/sales/${id}`;
            await this.$http.delete(url);
            
            this.makeToast('Transaction deleted successfully', { type: 'success' });
            
            // Close modal
            this.$bvModal.hide('transaction-details-modal');
            
            // Remove from local list to avoid refresh
            this.salesTransactions = this.salesTransactions.filter(tx => 
                (tx.ticketId && tx.ticketId !== id) || // Check ticketId match
                (!tx.ticketId && tx.sync_order_id !== id) // Or check sync_order_id if ticketId missing
            );
            
        } catch (error) {
            console.error('Delete failed:', error);
            this.makeToast('Failed to delete transaction', { type: 'error' });
        }
    },

    async fetchSalesData() {
      // Also fetch the specific transactions table
      this.fetchTransactions();

      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        const response = await this.$http.get('https://api.spotjockeys.com/api/metrics');
        const data = response.data;
        
        console.log('API Metrics Data:', data);

        let allRecords = [];
        if (Array.isArray(data)) allRecords = data;
        else if (data.rows) allRecords = data.rows; 
        else if (data.data) allRecords = data.data;
        else if (data.sales) allRecords = data.sales;

        console.log('Parsed Records:', allRecords.length, allRecords[0]);

        this.salesData = allRecords;
        this.processData();
        
      } catch (error) {
        this.errorMessage = `Error fetching metrics: ${error.message}`;
        console.error('Fetch error:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async copyToClipboard(text) {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            this.makeToast('URL copied to clipboard', { type: 'success' });
        } catch (err) {
            console.error('Failed to copy: ', err);
            this.makeToast('Failed to copy URL', { type: 'error' });
        }
    },

    getValueFromRow(row, ...keys) {
        if (!row) return 0;
        
        let val = null;
        
        // 1. Direct match
        for (let k of keys) {
            if (row[k] !== undefined && row[k] !== null) {
                val = row[k];
                break;
            }
        }
        
        // 2. Fuzzy match if no direct match
        if (val === null) {
            const fuzzyKey = Object.keys(row).find(k => keys.some(search => k.includes(search)));
            if (fuzzyKey) val = row[fuzzyKey];
        }

        if (val === null || val === undefined) return 0;

        // 3. Clean and Parse
        if (typeof val === 'string') {
            // Remove currency symbols, commas, etc.
            const cleanVal = val.replace(/[^0-9.-]/g, '');
            const parsed = parseFloat(cleanVal);
            return isNaN(parsed) ? 0 : parsed;
        }
        
        if (typeof val === 'number') return val;
        
        return 0;
    },

    makeToast(message, options) {
      this.$toast(message, {
        timeout: options.timeout || 5000,
        type: options.type || "info",
        position: "top-center",
        closeOnClick: true,
        pauseOnFocusLoss: true,
        pauseOnHover: true,
        draggable: true,
        draggablePercent: 0.6,
        showCloseButtonOnHover: false,
        hideProgressBar: true,
        closeButton: "button",
        icon: true,
        rtl: false,
      });
    },

    prepareDonutData(records) {
        const exchangesMap = { 'StubHub': 0, 'VividSeats': 0, 'GoTickets': 0, 'SeatGeek': 0, 'TickPick': 0 };
        const primariesMap = { 'ParkWhiz': 0, 'ParkMobile': 0, 'JustPark': 0, 'SpotHero': 0 };

        records.forEach(row => {
            // Exchanges
            exchangesMap['StubHub'] += this.getValueFromRow(row, 'stubhub');
            exchangesMap['VividSeats'] += this.getValueFromRow(row, 'vividseats');
            exchangesMap['GoTickets'] += this.getValueFromRow(row, 'gotickets');
            exchangesMap['SeatGeek'] += this.getValueFromRow(row, 'seatgeek');
            exchangesMap['TickPick'] += this.getValueFromRow(row, 'tickpick');

            // Primaries
            primariesMap['ParkWhiz'] += this.getValueFromRow(row, 'parkwhiz');
            primariesMap['ParkMobile'] += this.getValueFromRow(row, 'parkmobile');
            primariesMap['JustPark'] += this.getValueFromRow(row, 'justpark');
            primariesMap['SpotHero'] += this.getValueFromRow(row, 'spothero');
            // primariesMap['N/A'] += this.getValueFromRow(row, 'N/A');
        });

        this.exchangeDonutData = {
            labels: Object.keys(exchangesMap),
            datasets: [{
                data: Object.values(exchangesMap),
                backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b']
            }]
        };

        this.primaryDonutData = {
            labels: Object.keys(primariesMap),
            datasets: [{
                data: Object.values(primariesMap),
                backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e']
            }]
        };
    },

    prepareHourlyVolumeData(records, dateField = 'invoiceDate') {
        const hours = Array.from({length: 24}, (_, i) => i);
        const exchanges = ['StubHub', 'VividSeats', 'GoTickets', 'SeatGeek', 'TickPick'];
        const dataMap = {}; 
        
        hours.forEach(h => {
             dataMap[h] = {};
             exchanges.forEach(ex => dataMap[h][ex] = 0);
        });
        
        records.forEach(r => {
             const dStr = r.invoiceDate || r[dateField] || r.created_at || r.sale_date || r.date;
             if (!dStr) return;
             
             let hour;
             try {
                // Use Date object parsing which is more robust for ISO strings
                const d = new Date(dStr);
                if (!isNaN(d.getTime())) {
                     // Since the API returns "Fake UTC" (Eastern Time + Z), 
                     // d.getUTCHours() correctly extracts the Eastern Time hour (0-23).
                     hour = d.getUTCHours();
                } else if (String(dStr).indexOf('T') !== -1) {
                    // Fallback string parsing
                    hour = parseInt(dStr.split('T')[1].substring(0, 2));
                }
             } catch(e) { return; }
             
             if (hour === undefined || isNaN(hour)) return;

             exchanges.forEach(ex => {
                 let belongsToExchange = false;
                 
                 // If getValueFromRow returns > 0, it means this record likely has counts for this exchange (e.g. summarized data)
                 // or it is a flat record where the exchange column is present.
                 if (this.getValueFromRow(r, ex.toLowerCase()) > 0) {
                     belongsToExchange = true;
                 } 
                 // Checking customerDisplayName handles the standard transaction list format
                 else if (r.customerDisplayName === ex) {
                     belongsToExchange = true;
                 }

                 if (belongsToExchange) {
                     // User requested row count instead of quantity sum
                     dataMap[hour][ex] += 1;
                 }
             });
        });

        const datasets = exchanges.map((ex, i) => {
            return {
                label: ex,
                data: hours.map(h => dataMap[h][ex]),
                backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b'][i] 
            };
        });

        this.hourlyVolumeChartData = {
            labels: hours.map(h => {
                const ampm = h >= 12 ? 'pm' : 'am';
                const h12 = h % 12 || 12;
                return `${h12}${ampm}`;
            }),
            datasets: datasets
        };
    },

    prepareWeeklyVolumeData(records, dateField = 'created_at') {
        // Simple grouping by ISO Week or "Week of [Date]"
        const volumeByWeek = {};
        
        // Helper to get Sunday of the week for a date, using ET
        const getSunday = (dStr) => {
            if (!dStr) return null;
            let etDateStr;
            try {
               // The API returns "Fake UTC" (wall-clock ET time ending in Z).
               etDateStr = dStr.substring(0, 10);
            } catch(e) { return null; }

            // Create a date object that represents this calendar date (at noon to avoid DST boundaries)
            // Note: This creates a local date based on the ET string, effectively extracting the week relative to ET
            const date = new Date(etDateStr + 'T12:00:00'); 
            const day = date.getDay(); // 0 is Sunday
            const diff = date.getDate() - day; 
            
            // Adjust to Sunday
            const sunday = new Date(date);
            sunday.setDate(diff); // This handles month boundaries correctly
            
            // Format back to YYYY-MM-DD manually
            const y = sunday.getFullYear();
            const m = String(sunday.getMonth() + 1).padStart(2, '0');
            const d = String(sunday.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };
        
        // 1. Group Data
        records.forEach(r => {
             const qty = this.getValueFromRow(r, 'quantity', 'tickets') || 0;
             let dateStr = r[dateField] || r.created_at || r.sale_date || r.date;
             
             const sundayStr = getSunday(dateStr);
             if (sundayStr) {
                volumeByWeek[sundayStr] = (volumeByWeek[sundayStr] || 0) + qty;
             }
        });
        
        // 2. Sort Keys (Weeks)
        const sortedWeeks = Object.keys(volumeByWeek).sort();
        
        // 3. Format Labels (e.g. "Jan 12")
        const labels = sortedWeeks.map(w => {
            const [, m, d] = w.split('-');
            return `${parseInt(m)}/${parseInt(d)}`;
        });
        
        // 4. Data Array
        const data = sortedWeeks.map(w => volumeByWeek[w]);
        
        this.weeklyVolumeChartData = {
            labels: labels,
            datasets: [{
                label: 'Tickets',
                backgroundColor: '#94dcf0', // Light blue
                hoverBackgroundColor: '#22a7f0',
                borderColor: '#22a7f0',
                borderWidth: 1,
                borderRadius: 4, // Rounded bars if supported by chartjs version, otherwise ignored
                data: data
            }]
        };
    },

    calculateMetrics(records) {
        console.log('Calculating metrics for', records.length, 'records');
        if (records.length > 0) {
            console.log('Sample Record Keys:', Object.keys(records[0]));
            // Log what we extract for the first record
            const sample = records[0];
            const sampleSales = this.getValueFromRow(sample, 'sales', 'sale_amount', 'total_amount', 'price', 'amount', 'gross');
            console.log('Sample Extraction -> Sales:', sampleSales); 
        }

        this.summary.sales = records.reduce((acc, curr) => acc + this.getValueFromRow(curr, 'sales', 'sale_amount', 'total_amount', 'price', 'amount', 'gross'), 0);
        this.summary.tickets = records.reduce((acc, curr) => acc + this.getValueFromRow(curr, 'quantity', 'tickets', 'qty'), 0);
        
        this.summary.profit = records.reduce((acc, curr) => {
             const profit = this.getValueFromRow(curr, 'profit', 'gross_profit', 'net_profit');
             if (profit !== 0) return acc + profit;
             
             const sales = this.getValueFromRow(curr, 'sales');
             const cost = this.getValueFromRow(curr, 'unit_cost', 'cost_per_item', 'cost');
             const qty = this.getValueFromRow(curr, 'quantity', 'tickets') || 1;
             return acc + (sales - (cost * qty));
        }, 0);
    },

    prepareChartData(records, dateField) {
       // Group by date
       const profitByDate = {};
       const salesByDate = {};
       
       records.forEach(r => {
           let dateStr = r[dateField];
           
           if (!dateStr) return;
           try {
             // The API returns "Fake UTC" (wall-clock ET time ending in Z).
             // Use ET date for grouping to match the filter logic
             dateStr = String(dateStr).substring(0, 10);
           } catch (e) {
             // Fallback
             dateStr = String(dateStr).substring(0, 10);
           }
           
           const profit = this.calculateRowProfit(r);
           const sales = this.calculateRowSales(r);
           
           profitByDate[dateStr] = (profitByDate[dateStr] || 0) + profit;
           salesByDate[dateStr] = (salesByDate[dateStr] || 0) + sales;
       });

       // Sort dates
       const labels = Object.keys(profitByDate).sort();
       const profitData = labels.map(d => profitByDate[d]);

       this.trendChartData = {
          labels: labels,
          datasets: [
            {
              label: 'Daily Profit',
              backgroundColor: 'rgba(75, 192, 192, 0.5)',
              borderColor: 'rgba(75, 192, 192, 1)',
              borderWidth: 1,
              data: profitData
            }
          ]
       };
    },

    calculateRowSales(row) {
        return this.getValueFromRow(row, 'sales', 'sale_amount', 'total_amount', 'price', 'amount', 'gross');
    },
    
    calculateRowProfit(row) {
        const profit = this.getValueFromRow(row, 'profit', 'gross_profit', 'net_profit');
        if (profit !== 0) return profit;
        
        const sales = this.getValueFromRow(row, 'sales', 'sale_amount', 'total_amount', 'price');
        const cost = this.getValueFromRow(row, 'unit_cost', 'cost_per_item', 'cost');
        const qty = this.getValueFromRow(row, 'quantity', 'tickets') || 1;
        return sales - (cost * qty);
    },

    async fetchTransactions() {
      this.salesTransactionsLoading = true;
      try {
         const { startDate, endDate } = this.getDateRange();
         
         // Fetch a slightly wider range to account for timezone differences (UTC vs ET)
         // We filter precisely on the client side in filteredSalesTransactions
         const params = {};
         if (startDate) {
             const start = new Date(startDate);
             start.setDate(start.getDate() - 1); // -1 day buffer
             params.startDate = start.toISOString().split('T')[0];
         }
         if (endDate) {
             const end = new Date(endDate);
             end.setDate(end.getDate() + 1); // +1 day buffer
             params.endDate = end.toISOString().split('T')[0];
         }

         const response = await this.$http.get('https://api.spotjockeys.com/api/sales-trans', { params });
         this.salesTransactions = response.data;
         this.salesTxCurrentPage = 1;
         
         // Strictly filter the data for the Hourly Chart to match the selected range (removing the buffer)
         // This ensures we only show "Today's" volume if "Today" is selected, and not the buffer days.
         const chartTransactions = this.salesTransactions.filter(tx => {
             const dStr = tx.invoiceDate;
             if (!dStr) return false;
             try {
                // dStr is "Fake UTC" (e.g. 2026-01-19T15...Z), so substring(0,10) is the wall-clock ET date
                const etDateStr = dStr.substring(0, 10);
                // startDate and endDate are presumably "YYYY-MM-DD" strings from getDateRange()
                return etDateStr >= startDate && etDateStr <= endDate;
             } catch(e) { return false; }
         });

         // Generate Hourly Volume chart from transaction data
         this.prepareHourlyVolumeData(chartTransactions, 'invoiceDate');
      } catch (error) {
         console.error("Error fetching sales transactions:", error);
         // Optionally set error message or toast
      } finally {
         this.salesTransactionsLoading = false;
      }
    },

    formatColumnLabel(fieldName) {
      // Convert field names to readable labels
      return fieldName
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    },

    getDateRange() {
      // Get current "wall clock" time in Eastern Time
      // We create a Date object that effectively represents the ET numbers in local time context
      // This allows easy manipulation of days/months
      const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
      
      let startDate = new Date(now);
      let endDate = new Date(now);

      // Helper to format as YYYY-MM-DD
      const toISO = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
      };

      switch (this.selectedDateRange) {
        case 'today':
          // Start: Today 00:00 (ET)
          // End: Today 23:59 (ET)
          // We rely on string comparison, so just matching the date matches the whole day
          startDate = new Date(now);
          endDate = new Date(now);
          break;
        case 'yesterday':
          startDate.setDate(now.getDate() - 1);
          endDate = new Date(startDate);
          break;
        case 'last7days':
          startDate.setDate(now.getDate() - 6);
          endDate = new Date(now);
          break;
        case 'last30days':
          startDate.setDate(now.getDate() - 29);
          endDate = new Date(now);
          break;
        case 'thisMonth':
          startDate.setDate(1);
          endDate = new Date(now);
          break;
        case 'lastMonth':
          startDate.setDate(0); // Last day of previous month
          endDate = new Date(startDate); // End is last day
          startDate.setDate(1); // Start is 1st of that month
          break;
        case 'yearToDate':
          startDate.setMonth(0, 1); // Jan 1 of the current year
          endDate = new Date(now);
          break;
        case 'custom':
          // Custom dates from input are usually YYYY-MM-DD
          return {
            startDate: this.customStartDate,
            endDate: this.customEndDate
          };
      }

      return {
        startDate: toISO(startDate),
        endDate: toISO(endDate)
      };
    },

    onDateRangeChange() {
      if (this.selectedDateRange !== 'custom') {
        this.processData();
        this.fetchTransactions(); // Fetch new table data based on new range
      }
    },
    
    getChartDateRange() {
      const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
      let startDate = new Date(now);
      let endDate = new Date(now);
      
      const toISO = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
      };

      switch (this.chartDateRange) {
        case 'last7days':
          startDate.setDate(now.getDate() - 6);
          break;
        case 'last30days':
          startDate.setDate(now.getDate() - 29);
          break;
        case 'last3Months':
          startDate.setMonth(now.getMonth() - 2);
          startDate.setDate(1); // Optional: start from 1st? Or rolling 3 months? Let's do rolling
          // Rolling 90 days roughly
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 90);
          break;
        case 'last6Months':
          startDate.setDate(now.getDate() - 180);
          break;
        case 'thisYear':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
      }
      
      return {
          startDate: toISO(startDate),
          endDate: toISO(endDate)
      };
    },

    processChartDataOnly() {
        if (this.salesData.length === 0) return;
        
        const { startDate, endDate } = this.getChartDateRange();
        // console.log(`Filtering chart data between ${startDate} and ${endDate}`);
        
        let dateField = 'created_at';
        const sample = this.salesData[0];
        if (sample.created_at) dateField = 'created_at';
        else if (sample.sale_date) dateField = 'sale_date';
        else if (sample.date) dateField = 'date';
        else {
             // Robust detection matching processData
             const key = Object.keys(sample).find(k => k.toLowerCase().includes('date') || k.toLowerCase().includes('time'));
             if (key) dateField = key;
        }
        
        const filteredRecords = this.salesData.filter(r => {
             const dStr = r[dateField];
             if (!dStr) return false;
             try {
                // The API returns "Fake UTC". Use raw YYYY-MM-DD.
                const etDateStr = String(dStr).substring(0, 10);
                return etDateStr >= startDate && etDateStr <= endDate;
             } catch(e) { return false; }
        });
        
        // console.log(`Chart Records after filter: ${filteredRecords.length}`);
        this.prepareChartData(filteredRecords, dateField);
        this.prepareWeeklyVolumeData(filteredRecords, dateField);
    },

    processData() {
        // ... (Existing processData logic for table/summary)
        const { startDate, endDate } = this.getDateRange();
        // console.log(`Filtering data between ${startDate} and ${endDate} (ET)`);

        // Auto-detect date field if known ones missing
        let dateField = 'created_at';
        if (this.salesData.length > 0) {
            const sample = this.salesData[0];
            if (sample.created_at) dateField = 'created_at';
            else if (sample.sale_date) dateField = 'sale_date';
            else if (sample.date) dateField = 'date';
            else {
                // Try to find a field with 'date' or 'time' in the name
                const key = Object.keys(sample).find(k => k.toLowerCase().includes('date') || k.toLowerCase().includes('time'));
                if (key) dateField = key;
            }
        }

        const filteredRecords = this.salesData.filter(r => {
             const dStr = r[dateField];
             if (!dStr) return false;
             
             // Convert record timestamp to ET YYYY-MM-DD
             try {
                // The API returns "Fake UTC". Use raw YYYY-MM-DD.
                const etDateStr = String(dStr).substring(0, 10);
                return etDateStr >= startDate && etDateStr <= endDate;
             } catch(e) { return false; }
        });

        // console.log(`Records after filter: ${filteredRecords.length}`);

        this.filteredSales = filteredRecords;
        this.calculateMetrics(filteredRecords);
        this.prepareDonutData(filteredRecords);
        // this.prepareWeeklyVolumeData(filteredRecords, dateField); // Moved to processChartDataOnly
        // this.prepareChartData(filteredRecords, dateField); // <-- Removed, we now call processChartDataOnly
        
        // Also update chart initially
        this.processChartDataOnly();
        
        // Ensure transactions are fetched if custom date range was just finished being set?
        // Actually onDateRangeChange handles non-custom.
        // For custom, the inputs call processData directly. We should probably fetchTransactions there too?
        if (this.selectedDateRange === 'custom') {
           this.fetchTransactions();
        }
    },
    resolveStatus(tx) {
        // ParkWhiz rule: If primary is ParkWhiz, Qty is 2, and has valid URL -> DELIVERED
        if (tx.primary_name && tx.primary_name.toLowerCase().includes('parkwhiz') && tx.quantity == 2 && tx.pw_parking_pass_url && String(tx.pw_parking_pass_url).startsWith('https')) {
             return 'DELIVERED';
        }

        if (!tx.pw_parking_pass_url) return 'PENDING';
        if (!String(tx.pw_parking_pass_url).startsWith('https')) return 'ATTENTION';
        return (tx.sync_order_status || 'ATTENTION').toUpperCase();
    },
    getBadgeClass(status) {
        if (!status) return 'badge-danger bg-soft-danger text-danger';
        const s = status.toUpperCase();
        if (s === 'DELIVERED' || s === 'COMPLETED') return 'badge-success bg-soft-success text-success';
        if (s === 'CONFIRMED') return 'badge-info bg-soft-info text-info';
        if (s === 'NOT DELIVERED' || s === 'ATTENTION') return 'badge-danger bg-soft-danger text-danger';
        if (s === 'PENDING') return 'badge-warning bg-soft-warning text-warning';
        return 'badge-secondary bg-soft-secondary';
    },
    getBadgeIcon(status) {
        if (!status) return 'fa-exclamation-circle';
        const s = status.toUpperCase();
        if (s === 'DELIVERED' || s === 'COMPLETED') return 'fa-check';
        if (s === 'CONFIRMED') return 'fa-clipboard-check';
        if (s === 'NOT DELIVERED' || s === 'ATTENTION') return 'fa-exclamation-triangle';
        if (s === 'PENDING') return 'fa-clock';
        return 'fa-circle';
    },
    getStatusVariant(status) {
        if (!status) return 'danger';
        const s = status.toUpperCase();
        if (s === 'DELIVERED' || s === 'COMPLETED') return 'success';
        if (s === 'CONFIRMED') return 'info';
        if (s === 'PENDING') return 'warning';
        return 'danger';
    },
    formatDateShort(date) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return String(date);
      return d.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return String(date);
      
      // The API returns "Fake UTC" (wall clock ET values + Z).
      // To display the correct wall clock time (e.g. 15:54), we treat it as UTC and display as UTC.
      return d.toLocaleString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit'
      });
    },
    formatTime(date) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      
      return d.toLocaleTimeString('en-US', {
        timeZone: 'UTC',
        hour: 'numeric',
        minute: '2-digit'
      });
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    },

    getRowTooltip(tx) {
      const primary = tx.primary_name || tx.primary_location_name || 'N/A';
      return `Venue: ${tx.venue || 'N/A'}\nPrimary: ${primary}\nExchange: ${tx.customerDisplayName || 'N/A'}\nList Price: ${this.formatCurrency(tx.listPrice)}\nProfit: ${this.formatCurrency(tx.profit)}`;
    },

    salesTxPreviousPage() {
      if (this.salesTxCurrentPage > 1) {
        this.salesTxCurrentPage--;
      }
    },

    salesTxNextPage() {
      if (this.salesTxCurrentPage < this.salesTxTotalPages) {
        this.salesTxCurrentPage++;
      }
    },

    handleScroll() {
      this.showBackToTop = window.scrollY > 300;
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  },

  mounted() {
    this.fetchSalesData();
    this.startTimer();
    window.addEventListener('scroll', this.handleScroll);
  },
  
  beforeDestroy() {
    this.stopTimer();
    window.removeEventListener('scroll', this.handleScroll);
  }
};
</script>

<style scoped>
.sales-dashboard {
  padding: 20px;
}

.header-section {
  margin-bottom: 30px;
}

.filter-section {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 15px;
  flex-wrap: wrap;
}

.date-filters {
  display: flex;
  align-items: center;
  gap: 10px;
}

.custom-date-range {
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn:disabled {
  background-color: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner-sm {
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.summary-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.summary-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
}

.summary-card h4 {
  margin: 0 0 10px 0;
  color: #666;
  font-size: 14px;
  text-transform: uppercase;
}

.summary-value {
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.error-message {
  background-color: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.table-section {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.sales-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  white-space: nowrap;
}

.sales-table th,
.sales-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  font-size: 13px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 120px;
}

.sales-table th {
  background-color: #f8f9fa;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sales-table th:hover {
  background-color: #e9ecef;
}

.sales-table tbody tr:hover {
  background-color: #f5f5f5;
}

.sales-table tbody tr:nth-child(even) {
  background-color: #f8fffe;
  border-left: 4px solid #28a745;
}

.sales-table tbody tr:nth-child(odd) {
  background-color: #ffffff;
  border-left: 4px solid #e8f5e8;
}

.sales-table tbody tr:hover {
  background-color: #e8f8e8 !important;
  border-left-color: #20c997 !important;
}

.status-completed {
  color: #28a745;
  font-weight: bold;
}

.status-pending {
  color: #ffc107;
  font-weight: bold;
}

.status-cancelled {
  color: #dc3545;
  font-weight: bold;
}

.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-top: 1px solid #ddd;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.sort-indicator {
  margin-left: 5px;
  font-size: 12px;
}

.table-responsive {
  overflow-x: auto;
  min-width: 100%;
}

.title-col {
  min-width: 250px;
}

/* Compact table style for Sales Transactions */
.compact-table th, 
.compact-table td {
  font-size: 11px !important;
  padding: 2px 4px !important; /* Reduced padding */
  vertical-align: middle;
  white-space: nowrap;
}

.btn-xs {
  padding: 1px 5px !important;
  font-size: 10px !important;
  line-height: 1.2;
  border-radius: 3px;
}

.sales-table th {
  position: relative;
  resize: horizontal;
  overflow: auto;
}

.sales-table th:hover {
  background-color: #e9ecef;
  cursor: col-resize;
}

/* Specific column width adjustments */
.sales-table th:nth-child(1),
.sales-table td:nth-child(1) {
  min-width: 80px; /* ID columns */
}

.sales-table th:nth-child(2),
.sales-table td:nth-child(2) {
  min-width: 150px; /* Date columns */
}

.sales-table th:nth-child(3),
.sales-table td:nth-child(3) {
  min-width: 200px; /* Name/description columns */
}

.sales-table th:nth-child(4),
.sales-table td:nth-child(4) {
  min-width: 180px; /* Event/venue columns */
}

.sales-table th:nth-child(5),
.sales-table td:nth-child(5) {
  min-width: 100px; /* Quantity columns */
}

.sales-table th:nth-child(6),
.sales-table td:nth-child(6) {
  min-width: 120px; /* Amount columns */
}

.sales-table th:nth-child(7),
.sales-table td:nth-child(7) {
  min-width: 140px; /* Payment method columns */
}

.sales-table th:nth-child(8),
.sales-table td:nth-child(8) {
  min-width: 100px; /* Status columns */
}

/* URL columns need more space */
.sales-table td .url-link {
  max-width: 300px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.url-link {
  color: #007bff;
  text-decoration: none;
  word-break: break-all;
}

.url-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .filter-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .date-filters,
  .custom-date-range {
    justify-content: center;
  }

  .pagination-section {
    flex-direction: column;
    gap: 15px;
  }

  .sales-table {
    font-size: 11px;
  }

  .sales-table th,
  .sales-table td {
    padding: 6px 8px;
  }
}

.row-warning-red, .row-warning-red > td {
  background-color: #ffe6e6 !important;
}

.row-justpark, .row-justpark > td {
  background-color: #f3e5f5 !important; /* Light purple */
}

@media (min-width: 992px) {
  .col-lg-2 {
      flex: 0 0 16.666667%;
      max-width: 16.666667%;
  }
}

.chart-container {
    padding: 0;
}

/* LBD Card Styles Simulation */

.bg-soft-success { background-color: #d1e7dd !important; }
.bg-soft-warning { background-color: #fff3cd !important; }
.bg-soft-danger { background-color: #f8d7da !important; }
.bg-soft-info { background-color: #cff4fc !important; }
.text-warning-dark { color: #856404 !important; }
.text-danger-dark { color: #842029 !important; }

.icon-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}
.opacity-7 { opacity: 0.7; }
.card-stats .icon-big {
    font-size: 2.4em;
    min-height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.card-stats .numbers {
    font-size: 1.8em;
    text-align: right;
}

.card-stats .card-category {
    color: #9A9A9A;
    font-size: 13px;
    line-height: 1.3em;
    margin: 0;
    text-transform: capitalize;
}

.card-stats .card-title {
    margin: 0;
    color: #333333;
    font-weight: 400;
    font-size: clamp(1.2rem, 3vw, 2rem);
}

/* Touch-friendly Inputs */
.control-group {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    border: 1px solid #e9ecef;
    transition: all 0.2s ease;
}

.control-group:active, .control-group:focus-within {
    border-color: #007bff;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(0,123,255,0.1);
}

.select-wrapper {
    position: relative;
    display: inline-block;
}

.custom-select-modern {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background: transparent;
    border: none;
    padding-right: 20px;
    padding-left: 5px;
    font-size: 16px; /* 16px prevents iOS zoom on focus */
    font-weight: 600;
    color: #2c3e50;
    cursor: pointer;
    line-height: 1.5;
    outline: none;
    min-width: 100px;
}

.select-icon {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6c757d;
    font-size: 0.8rem;
}

.date-inputs-group {
    border: 1px solid #e9ecef;
    background-color: #f8f9fa !important;
    border-radius: 12px !important;
    padding: 6px 12px !important;
}

.date-inputs-group input[type="date"] {
    font-family: inherit;
    color: #495057;
}

/* Fix for date input on iOS */
input[type="date"] {
    -webkit-appearance: none;
    min-height: 30px;
}

/* Transaction Card Styles */
.transaction-card {
    border-radius: 12px !important;
    transition: all 0.2s ease;
    border: 1px solid #edf2f9 !important;
}
.transaction-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 .5rem 1rem rgba(0,0,0,.08) !important;
    border-color: #dbe2ef !important;
    cursor: pointer;
}
.bg-soft-primary {
    background-color: rgba(54, 185, 204, 0.1);
}
.text-primary-soft {
    color: #36b9cc;
}
.bg-soft-success {
    background-color: rgba(28, 200, 138, 0.1);
}
.bg-soft-warning {
    background-color: rgba(246, 194, 62, 0.1);
}
.bg-soft-danger {
    background-color: rgba(231, 74, 59, 0.1);
}
.bg-soft-secondary {
    background-color: #f8f9fa;
}
.badge-soft-secondary {
    background-color: #eaecf4;
    color: #858796;
}
.very-small {
    font-size: 0.65rem;
    letter-spacing: 0.5px;
}
.badge {
    padding: 0.4em 0.8em;
    font-weight: 600;
    border-radius: 6px;
}
.btn-icon {
    width: 36px;
    height: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

@media (min-width: 992px) {
    .border-left-lg {
        border-left: 1px solid #eaecf4;
    }
}
.btn-white {
    background-color: #fff;
    color: #4e73df;
    border-color: #dddfeb;
}
.btn-white:hover {
    background-color: #f8f9fa;
    color: #2e59d9;
    border-color: #d1d3e2;
}

.back-to-top {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  padding: 0;
  display: flex !important;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  z-index: 9999;
  cursor: pointer;
  transition: opacity 0.3s, transform 0.3s;
  opacity: 0.4;
}

.back-to-top:hover {
  transform: translateY(-3px);
  opacity: 1;
}

.status-option:hover {
    background-color: #f8f9fa;
}

.status-option:active {
    background-color: #e9ecef;
}

/* Larger touch targets for iPad/Tablet */
@media (hover: none) and (pointer: coarse) {
    .status-option {
        padding-top: 12px !important;
        padding-bottom: 12px !important;
        margin-bottom: 4px !important;
    }
    
    .status-option input[type="checkbox"] {
        transform: scale(1.2);
    }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.header-action-btn {
  width: 100%;
}
@media (min-width: 768px) {
  .header-action-btn {
    width: auto !important;
  }
}

/* Card Stats Typography */
.card-stats .card-category {
  font-size: 1rem !important;
}
.card-stats .card-title {
  font-size: 2rem !important;
  font-weight: 600;
}
</style>

<style scoped>
.status-filter-btn {
  height: 38px !important;
  border-radius: 8px !important;
  min-width: 120px !important;
  max-width: 180px !important;
  font-size: 15px !important;
  padding-left: 10px !important;
  padding-right: 10px !important;
}
.status-filter-dropdown {
  min-width: 120px !important;
  max-width: 200px !important;
  width: 100% !important;
}
</style>

<style scoped>
.purchase-queue-btn {
  background: rgba(0, 180, 255, 0.05) !important;
  color: #007fa3 !important;
  border: none !important;
  transition: background 0.15s, color 0.15s;
}
.purchase-queue-btn:hover {
  background: rgba(0, 180, 255, 0.22) !important;
  color: #005a73 !important;
}
.purchase-queue-btn:active,
.purchase-queue-btn:focus {
  background: rgba(0, 180, 255, 0.32) !important;
  color: #005a73 !important;
}
</style>
